import * as quicklink from "quicklink";
//import * as $ from "jquery";
import "waypoints/lib/jquery.waypoints.js";
import "waypoints/lib/shortcuts/inview.js";
import "waypoints/lib/shortcuts/sticky.js";
import "waypoints/lib/shortcuts/infinite.js";

declare global {
    interface JQuery {
        waypoint(handler: (this: Waypoint, direction?: string) => void, options?: WaypointOptionsJQuery): JQuery;
        waypoint(options?: WaypointOptionsJQuery): JQuery;
    }

    interface WaypointOptionsJQuery extends WaypointOptionsBase {
        handler?: ((this: Waypoint, direction?: string) => void) | undefined;
        context?: HTMLElement | string | undefined;
    }

}

type FunctionArgs = any[];

interface PageInfo {
    isFrontPage: boolean;
    isArchive: boolean;
    isHome: boolean;
    isSingle: boolean;
    isCategory: boolean;
    isMobile: boolean;
    baseUrl: string;
    title: string;
    currentUrl: string;
    ajaxUrl: string;
    debug: boolean;
    facebookPixelId: string;
    googleAnalyticsId: string;
    hideExpandPanel: () => void;
}


interface ParallaxElement extends HTMLElement {
    initialTop?: number;
    initialBottom?: number;
    initialHeight?: number;
}

class FloatingNewsletter {
    private widget: HTMLElement | null = null;
    private toggle: HTMLElement | null = null;
    private panel: HTMLElement | null = null;
    private minimizeBtn: HTMLElement | null = null;
    private form: HTMLFormElement | null = null;
    private emailInput: HTMLInputElement | null = null;
    private submitBtn: HTMLButtonElement | null = null;
    private submitText: HTMLElement | null = null;
    private submitLoading: HTMLElement | null = null;
    private messageContainer: HTMLElement | null = null;
    private isNewsletterExpanded: boolean = false;
    private isNewsletterSubscribed: boolean = false;
    private autoCloseTimer: number | null = null;
    private userHasInteracted: boolean = false;
    private newsletterDelay: number;

    constructor(delay: number = 10000) {
        this.newsletterDelay = delay;
    }

    init(): void {
        // Check if user has already subscribed
        const hasSubscribed = localStorage.getItem('woyage_newsletter_subscribed');

        // Check if user has manually closed the newsletter
        const hasClosed = localStorage.getItem('woyage_newsletter_closed');

        if (hasSubscribed === 'true') {
            return; // Don't initialize if already subscribed
        }

        this.initElements();

        if (!this.widget) {
            return; // Widget not found on this page
        }

        this.bindEvents();

        if (hasClosed === 'true') {
            // Show widget in collapsed state only (don't auto-expand)
            this.showWidgetCollapsed();
        } else {
            // Show widget and auto-expand after delay
            this.showWidgetAfterDelay();
        }
    }

    private initElements(): void {
        this.widget = document.getElementById('floating-newsletter-widget');
        this.toggle = document.getElementById('floating-newsletter-toggle');
        this.panel = document.getElementById('floating-newsletter-panel');

        if (!this.widget || !this.toggle || !this.panel) {
            return;
        }

        this.minimizeBtn = this.panel.querySelector('.floating-newsletter-minimize') as HTMLElement;
        this.form = this.panel.querySelector('#floating-newsletter-form') as HTMLFormElement;
        this.emailInput = this.panel.querySelector('#floating_newsletter_email') as HTMLInputElement;
        this.submitBtn = this.panel.querySelector('.floating-newsletter-submit') as HTMLButtonElement;
        this.submitText = this.panel.querySelector('.floating-newsletter-submit-text') as HTMLElement;
        this.submitLoading = this.panel.querySelector('.floating-newsletter-submit-loading') as HTMLElement;
        this.messageContainer = this.panel.querySelector('.floating-newsletter-message') as HTMLElement;
    }

    private bindEvents(): void {
        if (!this.widget) return;

        // Toggle click - expand from collapsed state
        this.toggle?.addEventListener('click', () => this.expandWidget());

        // Minimize button click - collapse to button
        this.minimizeBtn?.addEventListener('click', () => this.collapseWidget());

        // Track user interaction events
        this.widget.addEventListener('click', () => this.markUserInteraction());
        this.emailInput?.addEventListener('focus', () => this.markUserInteraction());
        this.emailInput?.addEventListener('input', () => this.markUserInteraction());
        this.form?.addEventListener('submit', () => this.markUserInteraction());

        // Form submission
        this.form?.addEventListener('submit', (e) => this.handleFormSubmit(e));

        // Hide when clicking outside (only in expanded state)
        document.addEventListener('click', (e) => this.handleOutsideClick(e));
    }

    private showWidgetAfterDelay(): void {
        setTimeout(() => {
            this.showWidget();
        }, this.newsletterDelay);
    }

    private showWidget(): void {
        if (!this.widget || !this.panel || !this.toggle || !this.emailInput) return;

        // Set initial state to expanded
        this.isNewsletterExpanded = true;
        this.widget.classList.add('show', 'expanded');
        this.widget.style.display = 'block';
        this.panel.style.display = 'block';
        this.toggle.style.display = 'none';

        // Focus on email input
        setTimeout(() => {
            this.emailInput?.focus();
        }, 300);

        // Auto-collapse after 10 seconds if no interaction
        this.autoCloseTimer = window.setTimeout(() => {
            if (this.isNewsletterExpanded && !this.userHasInteracted) {
                this.collapseWidget();
            }
        }, 10000);
    }

    private showWidgetCollapsed(): void {
        if (!this.widget || !this.panel || !this.toggle) return;

        // Set initial state to collapsed
        this.isNewsletterExpanded = false;
        this.widget.classList.add('show', 'previously-closed');
        this.widget.style.display = 'block';
        this.panel.style.display = 'none';
        this.toggle.style.display = 'flex';
    }

    private expandWidget(): void {
        if (this.isNewsletterExpanded || !this.widget || !this.panel || !this.toggle || !this.emailInput) return;

        this.isNewsletterExpanded = true;
        this.widget.classList.add('expanded');
        this.widget.classList.remove('previously-closed');
        this.panel.style.display = 'block';
        this.toggle.style.display = 'none';

        // Clear the closed state since user is actively engaging with the newsletter
        localStorage.removeItem('woyage_newsletter_closed');

        // Focus on email input
        setTimeout(() => {
            this.emailInput?.focus();
        }, 300);
    }

    private markUserInteraction(): void {
        if (!this.userHasInteracted) {
            this.userHasInteracted = true;
            if (this.autoCloseTimer) {
                clearTimeout(this.autoCloseTimer);
                this.autoCloseTimer = null;
            }
        }
    }

    private collapseWidget(): void {
        if (!this.isNewsletterExpanded || !this.widget || !this.panel || !this.toggle) return;

        this.isNewsletterExpanded = false;
        this.widget.classList.remove('expanded');

        // Remember that user manually closed the newsletter
        localStorage.setItem('woyage_newsletter_closed', 'true');

        setTimeout(() => {
            if (this.panel && this.toggle) {
                this.panel.style.display = 'none';
                this.toggle.style.display = 'flex';
            }
        }, 300);
    }

    private hideWidget(): void {
        if (!this.widget) return;

        this.widget.classList.remove('show');
        setTimeout(() => {
            if (this.widget) {
                this.widget.style.display = 'none';
            }
        }, 300);

        localStorage.setItem('woyage_newsletter_subscribed', 'true');
    }

    private handleFormSubmit(e: Event): void {
        e.preventDefault();

        if (!this.emailInput) return;

        const email = this.emailInput.value.trim();

        if (!email) {
            this.showMessage('Inserisci il tuo indirizzo email.', 'danger');
            return;
        }

        if (!this.isValidEmail(email)) {
            this.showMessage('Inserisci un indirizzo email valido.', 'danger');
            return;
        }

        // Show loading state
        this.setLoadingState(true);

        // Get nonce value
        const nonceElement = document.getElementById('floating_newsletter_nonce') as HTMLInputElement;
        const nonce = nonceElement ? nonceElement.value : '';

        // Send AJAX request
        fetch((window as any).pageInfo?.ajaxUrl || '/wp-admin/admin-ajax.php', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
                action: 'newsletter_subscription',
                email: email,
                source: 'floating_newsletter',
                nonce: nonce
            })
        })
            .then(response => response.json())
            .then(data => {
                this.setLoadingState(false);

                if (data.success) {
                    this.isNewsletterSubscribed = true;
                    this.showMessage('Perfetto! Ti sei iscritto alla newsletter.', 'success');
                    if (this.emailInput) {
                        this.emailInput.value = '';
                    }

                    // Hide widget permanently after successful subscription
                    setTimeout(() => {
                        this.hideWidget();
                    }, 3000);

                } else {
                    this.showMessage(data.data || 'Si è verificato un errore. Riprova più tardi.', 'danger');
                }
            })
            .catch(error => {
                this.setLoadingState(false);
                console.error('Newsletter subscription error:', error);
                this.showMessage('Si è verificato un errore. Riprova più tardi.', 'danger');
            });
    }

    private setLoadingState(loading: boolean): void {
        if (!this.submitBtn || !this.submitText || !this.submitLoading) return;

        if (loading) {
            this.submitBtn.disabled = true;
            this.submitText.style.display = 'none';
            this.submitLoading.style.display = 'inline';
        } else {
            this.submitBtn.disabled = false;
            this.submitText.style.display = 'inline';
            this.submitLoading.style.display = 'none';
        }
    }

    private showMessage(message: string, type: string): void {
        if (!this.messageContainer) return;

        this.messageContainer.innerHTML = `<div class="alert alert-${type} alert-sm mb-0 mt-3 p-2 small">${message}</div>`;

        // Auto-hide success messages
        if (type === 'success') {
            setTimeout(() => {
                if (this.messageContainer) {
                    this.messageContainer.innerHTML = '';
                }
            }, 5000);
        }
    }

    private isValidEmail(email: string): boolean {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    private handleOutsideClick(e: Event): void {
        if (this.isNewsletterExpanded &&
            this.widget &&
            !this.widget.contains(e.target as Node) &&
            !(e.target as Element).closest('#floating-newsletter-widget')) {
            this.collapseWidget();
        }
    }
}

class PageScript {
    info: PageInfo;
    private imageObserver: IntersectionObserver | null = null;

    constructor(info: PageInfo) {
        this.info = info;
    }

    init() {

        $("body").css('opacity', 1);

        //this.setupCookieConsent();
        //setupCookieConsent();
        this.initQuicklink();
        //this.initWaypoints();
        this.setupScrollAnimations();
        this.handleSmartSticky();
        this.handleNoScroll();
        this.handleOffcanvasNavbar();
        this.handleExpandPanels();
        this.handleNavbarWaypoints();
        //this.handleCollapseNavbar();
        this.handleFullHeightItems();
        //this.handleCarouselCaptionAnimation();
        //this.handleCarouselSwipe();
        this.handleParallax();
        this.handleSelectOnClickInputs();
        this.handleItinerary();
        this.handleNewsletterForm();
        this.handleFloatingNewsletter();
        this.handleLinkedHeightElements();
        this.handleLazyImages();
        this.handleBookingErrors();

        //this.setupCounterUp();

        if (this.info.debug) {
            this.log("Debug mode");
            $("#viewport-helper").show();
        }

    }

    private handleLazyImages() {
        this.log("handleLazyImages");

        // Check if IntersectionObserver is supported in the browser
        if (!('IntersectionObserver' in window)) {
            // Fallback for browsers that don't support IntersectionObserver
            this.loadAllLazyImages();
            return;
        }

        // Create options for IntersectionObserver
        const options = {
            // Start loading when image is 200px before it appears in viewport
            rootMargin: '200px 0px',
            threshold: 0.01 // Trigger when at least 1% of the element is visible
        };

        // Create an observer instance
        this.imageObserver = new IntersectionObserver((entries, observer) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const element = entry.target;

                    // Handle both <img> elements with lazy-src and elements with lazy-background-src
                    if (element.tagName.toLowerCase() === 'img') {
                        this.loadLazyImage(element as HTMLImageElement);
                    } else {
                        this.loadLazyBackgroundImage(element as HTMLElement);
                    }

                    // Stop observing the element once it's loaded
                    observer.unobserve(element);
                }
            });
        }, options);

        // Start observing all images with lazy-src attribute
        const lazyImages = document.querySelectorAll('img[lazy-src]');
        lazyImages.forEach(image => {
            // Add a placeholder or loading state if needed
            if (!image.getAttribute('src')) {
                image.setAttribute('src', 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"%3E%3C/svg%3E');
            }

            // Optional: Add loading class for styling
            image.classList.add('lazy-loading');

            // Start observing this image
            this.imageObserver.observe(image);
        });

        // Start observing all elements with lazy-background-src attribute
        const lazyBackgroundElements = document.querySelectorAll('[lazy-background-src]');
        lazyBackgroundElements.forEach(element => {
            // Optional: Add loading class for styling
            element.classList.add('lazy-loading');

            // Start observing this element
            this.imageObserver.observe(element);
        });

        // Handle images that might be added to the DOM later
        this.setupLazyImageMutationObserver();
    }

    private loadLazyImage(image: HTMLImageElement): void {
        const lazySrc = image.getAttribute('lazy-src');

        if (!lazySrc) return;

        // Create a new image element to preload the image
        const preloadImage = new Image();

        // Set up load event handler
        preloadImage.onload = () => {
            // Update the src only after the image has been loaded
            image.setAttribute('src', lazySrc);

            // Remove the lazy-src attribute to prevent reloading
            image.removeAttribute('lazy-src');

            // Remove the loading class
            image.classList.remove('lazy-loading');

            // Add loaded class for potential animations
            image.classList.add('lazy-loaded');

            // Dispatch a custom event that can be listened to
            image.dispatchEvent(new CustomEvent('lazyloaded'));
        };

        // Set up error event handler
        preloadImage.onerror = () => {
            // Keep the original src if it exists, or use a fallback
            if (!image.getAttribute('src') || image.getAttribute('src').includes('data:image/svg')) {
                image.setAttribute('src', image.getAttribute('data-fallback') || '');
            }

            // Remove the loading class
            image.classList.remove('lazy-loading');

            // Add error class
            image.classList.add('lazy-error');

            // Log error if in debug mode
            this.log('Error loading lazy image:', lazySrc);
        };

        // Start loading the image
        preloadImage.src = lazySrc;
    }

    private loadLazyBackgroundImage(element: HTMLElement): void {
        const lazySrc = element.getAttribute('lazy-background-src');

        if (!lazySrc) return;

        // Create a new image element to preload the image
        const preloadImage = new Image();

        // Set up load event handler
        preloadImage.onload = () => {
            // Apply the background image
            element.style.backgroundImage = `url('${lazySrc}')`;

            // Remove the lazy-background-src attribute to prevent reloading
            element.removeAttribute('lazy-background-src');

            // Remove the loading class
            element.classList.remove('lazy-loading');

            // Add loaded class for potential animations
            element.classList.add('lazy-loaded');

            // Dispatch a custom event that can be listened to
            element.dispatchEvent(new CustomEvent('lazyloaded'));
        };

        // Set up error event handler
        preloadImage.onerror = () => {
            // Keep the original background if it exists, or use a fallback
            if (element.getAttribute('data-fallback-background')) {
                element.style.backgroundImage = `url('${element.getAttribute('data-fallback-background')}')`;
            }

            // Remove the loading class
            element.classList.remove('lazy-loading');

            // Add error class
            element.classList.add('lazy-error');

            // Log error if in debug mode
            this.log('Error loading lazy background image:', lazySrc);
        };

        // Start loading the image
        preloadImage.src = lazySrc;
    }

    private loadAllLazyImages(): void {
        // Fallback method for browsers without IntersectionObserver
        // Immediately load all lazy images
        document.querySelectorAll('img[lazy-src]').forEach(image => {
            const img = image as HTMLImageElement;
            const lazySrc = img.getAttribute('lazy-src');

            if (lazySrc) {
                img.setAttribute('src', lazySrc);
                img.removeAttribute('lazy-src');
            }
        });

        document.querySelectorAll('[lazy-background-src]').forEach(element => {
            const lazySrc = element.getAttribute('lazy-background-src');

            if (lazySrc) {
                (element as HTMLElement).style.backgroundImage = `url('${lazySrc}')`;
                element.removeAttribute('lazy-background-src');
            }
        });
    }

    private setupLazyImageMutationObserver(): void {
        // Watch for new nodes added to the DOM that might contain lazy images
        if (!('MutationObserver' in window) || !this.imageObserver) return;

        const mutationObserver = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                if (mutation.type === 'childList' && mutation.addedNodes.length) {
                    mutation.addedNodes.forEach(node => {
                        // Check if the added node is an Element
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            // If the element itself has lazy-src or lazy-background-src
                            if ((node as Element).hasAttribute('lazy-src') ||
                                (node as Element).hasAttribute('lazy-background-src')) {

                                if ((node as Element).tagName.toLowerCase() === 'img') {
                                    // Add placeholder for images
                                    if (!(node as HTMLImageElement).getAttribute('src')) {
                                        (node as HTMLImageElement).setAttribute('src', 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"%3E%3C/svg%3E');
                                    }
                                }

                                (node as Element).classList.add('lazy-loading');
                                this.imageObserver!.observe(node as Element);
                            }

                            // Check for any lazy-src or lazy-background-src elements inside the added node
                            const lazyImagesInNode = (node as Element).querySelectorAll('img[lazy-src]');
                            const lazyBackgroundsInNode = (node as Element).querySelectorAll('[lazy-background-src]');

                            lazyImagesInNode.forEach(image => {
                                if (!image.getAttribute('src')) {
                                    image.setAttribute('src', 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"%3E%3C/svg%3E');
                                }
                                image.classList.add('lazy-loading');
                                this.imageObserver!.observe(image);
                            });

                            lazyBackgroundsInNode.forEach(element => {
                                element.classList.add('lazy-loading');
                                this.imageObserver!.observe(element);
                            });
                        }
                    });
                }
            });
        });

        // Start observing the document body for DOM changes
        mutationObserver.observe(document.body, {
            childList: true,
            subtree: true
        });
    }

    private handleLinkedHeightElements() {
        // Create a function to calculate and set heights
        const setLinkedHeights = () => {
            // Set the height of all elements with the same data-linked-height attribute to the height of the first one
            $('[data-linked-height]').each(function () {
                var $this = $(this);
                var linkedHeight = $this.data('linked-height');
                var $linkedElements = $('[data-linked-height="' + linkedHeight + '"]');

                // Reset heights first to get natural height
                $linkedElements.css('height', '');

                // Get the height of the tallest element
                var maxHeight = 0;
                $linkedElements.each(function () {
                    var height = $(this).outerHeight();
                    if (height > maxHeight) {
                        maxHeight = height;
                    }
                });

                // Set the height of all linked elements
                if (maxHeight > 0) {
                    $linkedElements.css('height', maxHeight + 'px');
                }
            });
        };

        // Run on page load
        setLinkedHeights();

        // Run on window resize with debounce
        let resizeTimer;
        $(window).on('resize', function () {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(setLinkedHeights, 250);
        });
    }

    private handleItinerary() {
        // Check if we are on the itinerary page (body has got the .single-itinerary class)
        if ($('body.single-itinerary').length == 0) return;

        // Inizializza le progress bar
        initProgressCircles();

        // Configura Waypoints per l'animazione al caricamento
        var waypointOffset = '90%';

        var waypoint = new Waypoint({
            element: $('.stats-container')[0],
            handler: function (direction) {
                if (direction === 'down') {
                    animateProgressCircles();
                    this.destroy(); // Esegui l'animazione una sola volta
                }
            },
            offset: waypointOffset
        });


        function initProgressCircles() {
            $('.stat-item').each(function () {
                var $this = $(this);
                var color = $this.data('color');

                // Configura il colore del cerchio
                $this.find('.progress-circle-bg').css('stroke', color);
                $this.find('.progress-circle-path').css('stroke', color);
            });
        }

        function animateProgressCircles() {
            $('.stat-item').each(function () {
                var $this = $(this);
                var value = $this.data('value');
                var $path = $this.find('.progress-circle-path');
                var $percentage = $this.find('.stat-percentage');

                // Calcolo della lunghezza del percorso e dell'offset
                var pathLength = 251.2; // 2 * Math.PI * 40 (raggio del cerchio)
                var dashOffset = pathLength * (1 - value / 100);

                // Contatore per l'animazione della percentuale
                var count = 0;
                var increment = value / 50;

                // Animazione del cerchio
                $path.css({
                    'stroke-dasharray': pathLength,
                    'stroke-dashoffset': pathLength
                }).animate({
                    'stroke-dashoffset': dashOffset
                }, {
                    duration: 1500,
                    easing: 'easeOutCubic',
                    step: function (now, fx) {
                        // Calcola la percentuale corrente in base alla posizione dell'animazione
                        var percentage = Math.round(100 - (now / pathLength * 100));
                        $percentage.text(percentage + '%');
                    }
                });
            });
        }

        // Aggiungi easing personalizzato se non è disponibile
        if (!$.easing.easeOutCubic) {
            $.extend($.easing, {
                easeOutCubic: function (x, t, b, c, d) {
                    return c * ((t = t / d - 1) * t * t + 1) + b;
                }
            });
        }
    }

    private handleNewsletterForm() {
        $('#newsletter-form').on('submit', (e) => {
            e.preventDefault();

            const emailField = $('#newsletter_email');
            const messageContainer = $('.newsletter-message');

            // Validate form
            if (!emailField.val()) {
                messageContainer.html('<div class="alert alert-danger mt-2">Inserisci il tuo indirizzo email.</div>');
                return;
            }

            // Show loading message
            messageContainer.html('<div class="alert alert-info mt-2">Iscrizione in corso...</div>');

            // Send AJAX request
            $.ajax({
                url: this.info.ajaxUrl,
                type: 'POST',
                data: {
                    action: 'newsletter_subscription',
                    email: emailField.val(),
                    nonce: $('#newsletter_nonce').val()
                },
                success: (response) => {
                    if (response.success) {
                        // Show success message
                        messageContainer.html('<div class="alert alert-success mt-2">' + response.data + '</div>');
                        // Reset form
                        emailField.val('');
                    } else {
                        // Show error message
                        messageContainer.html('<div class="alert alert-danger mt-2">' + response.data + '</div>');
                    }
                },
                error: () => {
                    // Show general error message
                    messageContainer.html('<div class="alert alert-danger mt-2">Si è verificato un errore. Riprova più tardi.</div>');
                }
            });
        });
    }

    private handleBookingErrors() {
        this.log("handleBookingErrors");

        // Get URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const bookingError = urlParams.get('booking_error');

        if (bookingError) {
            let errorMessage = '';
            let errorIcon = 'bi-exclamation-triangle-fill';
            let isLoginRequired = false;
            let redirectTo = urlParams.get('redirect_to') || '';

            // Map error codes to user-friendly messages
            switch (bookingError) {
                case 'not_logged_in':
                    errorMessage = 'Per accedere a questa prenotazione è necessario effettuare il login.';
                    errorIcon = 'bi-box-arrow-in-right';
                    isLoginRequired = true;
                    break;
                case 'access_denied':
                    errorMessage = 'Non hai il permesso di accedere a questa prenotazione.';
                    break;
                default:
                    errorMessage = 'Si è verificato un errore durante l\'elaborazione della richiesta.';
                    break;
            }

            // Create and show modal
            this.showBookingErrorModal(errorMessage, errorIcon, isLoginRequired, redirectTo);
        }
    }

    private showBookingErrorModal(message: string, icon: string, isLoginRequired: boolean = false, redirectTo: string = '') {
        // Build login URL for wp-login.php with redirect
        const loginUrl = redirectTo
            ? `/wp-login.php?redirect_to=${encodeURIComponent(redirectTo)}`
            : '/wp-login.php';

        // Determine modal style based on error type
        const headerBorderClass = isLoginRequired ? 'border-warning' : 'border-danger';
        const titleColorClass = isLoginRequired ? 'text-warning' : 'text-danger';
        const titleText = isLoginRequired ? 'Accesso Richiesto' : 'Errore di Accesso';
        const alertClass = isLoginRequired ? 'alert-warning' : 'alert-danger';

        // Build footer buttons
        const footerHtml = isLoginRequired
            ? `<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
                   <i class="bi bi-x-lg me-2"></i>Chiudi
               </button>
               <a href="${loginUrl}" class="btn btn-primary">
                   <i class="bi bi-box-arrow-in-right me-2"></i>Accedi
               </a>`
            : `<button type="button" class="btn btn-primary" data-bs-dismiss="modal">
                   <i class="bi bi-check-lg me-2"></i>Ho capito
               </button>`;

        // Create modal HTML
        const modalHtml = `
            <div class="modal fade" id="bookingErrorModal" tabindex="-1" aria-labelledby="bookingErrorModalLabel" aria-hidden="true">
                <div class="modal-dialog">
                    <div class="modal-content">
                        <div class="modal-header ${headerBorderClass}">
                            <div class="modal-title fs-5 fw-bold ${titleColorClass}" id="bookingErrorModalLabel">
                                <i class="${icon} me-2"></i>${titleText}
                            </div>
                            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div class="modal-body">
                            <div class="alert ${alertClass} d-flex align-items-center" role="alert">
                                <i class="${icon} me-2"></i>
                                <div>${message}</div>
                            </div>
                        </div>
                        <div class="modal-footer">
                            ${footerHtml}
                        </div>
                    </div>
                </div>
            </div>
        `;

        // Remove existing modal if present
        $('#bookingErrorModal').remove();

        // Add modal to body
        $('body').append(modalHtml);

        // Show modal
        const modal = new (window as any).bootstrap.Modal(document.getElementById('bookingErrorModal'));
        modal.show();

        // Handle modal close event (only remove URL params, don't reload for login flow)
        $('#bookingErrorModal').on('hidden.bs.modal', () => {
            if (!isLoginRequired) {
                this.removeBookingErrorFromUrl();
            } else {
                // Just clean the URL without reloading
                const url = new URL(window.location.href);
                url.searchParams.delete('booking_error');
                url.searchParams.delete('redirect_to');
                window.history.replaceState({}, document.title, url.toString());
            }
            $('#bookingErrorModal').remove();
        });
    }

    private removeBookingErrorFromUrl() {
        const url = new URL(window.location.href);
        url.searchParams.delete('booking_error');

        // Update URL without reload, then reload to ensure clean state
        window.history.replaceState({}, document.title, url.toString());
        window.location.reload();
    }

    private handleFloatingNewsletter() {
        this.log("handleFloatingNewsletter");

        // Get newsletter delay from global configuration
        const newsletterDelay = (window as any).pageInfo?.newsletterDelay || 10000;

        // Initialize the floating newsletter widget
        const floatingNewsletter = new FloatingNewsletter(newsletterDelay);
        floatingNewsletter.init();

        // Track newsletter interactions for analytics
        $(document).on('click', '.floating-newsletter-minimize', () => {
            this.log("Floating newsletter minimized");
            // Could send analytics event here
        });

        $(document).on('submit', '#floating-newsletter-form', () => {
            this.log("Floating newsletter form submitted");
            // Could send analytics event here
        });
    }

    // All inputs that have got data-select-on-click attribute set to 1 or true, will have their content selected when clicked
    private handleSelectOnClickInputs() {

        $('input[data-select-on-click="1"],input[data-select-on-click="true"]').on('click', function () {
            $(this).trigger('select');
        });

        $('textarea[data-select-on-click="1"],textarea[data-select-on-click="true"]').on('click', function () {
            $(this).trigger('select');
        });

    }

    private handleNavbarWaypoints() {
        var navbar = $(".navbar");
        var invertTrigger = $(".navbar-invert-trigger");

        if (navbar.length == 0) return;

        if ($('body.static-header').length > 0) {
            navbar.addClass('inverted');
            $("#nav-search-box").show();
            return;
        }

        // Se non c'è un trigger specifico, usiamo lo scroll semplice
        if (invertTrigger.length == 0) {
            $(window).on('scroll', function () {
                var scrollTop = $(window).scrollTop() || 0;
                if (scrollTop > 10) { // Considera scrollata dopo 10px
                    navbar.addClass('inverted');
                    $("#nav-search-box").slideDown();
                } else {
                    navbar.removeClass('inverted');
                    $("#nav-search-box").slideUp();
                }
            });

            // Eseguiamo la verifica anche all'inizio
            var initialScrollTop = $(window).scrollTop() || 0;
            if (initialScrollTop > 10) {
                navbar.addClass('inverted');
                $("#nav-search-box").slideDown();
            }

            return;
        }

        // Continua con il comportamento esistente per invertTrigger
        var navbarHeight = navbar.outerHeight();
        var searchBoxHeight = $("#home-search-box").outerHeight() || 0;

        var waypoint = new Waypoint({
            element: invertTrigger[0],
            handler: function (direction) {
                if (direction == 'down') {
                    navbar.addClass('inverted');
                    // Show the #search-box
                    $("#nav-search-box").slideDown();
                } else {
                    navbar.removeClass('inverted');
                    // Hide the #search-box
                    $("#nav-search-box").slideUp();
                }
            },
            offset: function () {
                return -this.element.clientHeight + navbarHeight + searchBoxHeight / 2;
            }
        });
    }

    /*private setupCounterUp() {
        this.log("setupCounterUp");

        // Foreach element with class counter, create a new Waypoint
        const el = $('.counter');
        for (var n = 0; n < el.length; n++) {
            this.log("counter " + n);
            this.setupCounterUpElement(el[n]);
        }
    }

    private setupCounterUpElement(el: any) {
        this.log("setupCounterUpElement");

        // get data-duration and data-delay
        const duration = $(el).data('duration') || 2000
        const delay = $(el).data('delay') || 16

        // Setup new waypoint
        const waypoint = new Waypoint({
            element: el,
            handler: () => {

                this.log("Firing waypoint of counter " + el);

                // Start counter
                counterUp(el, {
                    duration: duration,
                    delay: delay
                });

                // Destroy waypoint
                waypoint.destroy();

            },
            offset: 'bottom-in-view'
        })


    }
*/
    private handleParallax() {

        function throttle(fn: (...args: FunctionArgs) => void, delay: number): (...args: FunctionArgs) => void {
            let isThrottling = false;
            let context: any;
            let args: FunctionArgs;

            function execute() {
                if (isThrottling) {
                    context = this;
                    args = arguments as any;
                } else {
                    fn.apply(this, arguments);
                    isThrottling = true;
                    setTimeout(() => {
                        isThrottling = false;
                        if (args) {
                            execute.apply(context, args);
                            args = context = null;
                        }
                    }, delay);
                }
            }
            return execute;
        }

        let fullScreenElems: ParallaxElement[] = [];

        function onWindowResize(): void {
            if ($('.js-parallaxed-wide').length) {
                fullScreenElems.forEach(e => $(e).css({ height: "100%", position: "relative" }));
                addParallax();
            }
        }

        function addParallaxedStyles(): void {
            const windowWidth = $(window).width();
            if (windowWidth && windowWidth >= 768) {
                const scrollTop = $(window).scrollTop() || 0;
                fullScreenElems.forEach(e => {
                    if (scrollTop >= e.initialTop!) {
                        $(e).css({ position: "fixed", top: 0, width: "100%", height: `${e.initialHeight}px` });
                    }
                    if (scrollTop >= e.initialBottom! || scrollTop < e.initialTop!) {
                        $(e).css({ position: "relative" });
                    }
                });
            }
        }

        const throttledFunction = throttle(addParallaxedStyles, 16);

        function addParallax(): void {
            fullScreenElems = Array.from($('.parallaxed')) as ParallaxElement[];

            if (fullScreenElems.length) {
                const scrollTop = $(window).scrollTop() || 0;
                const windowWidth = $(window).width() || 0;

                fullScreenElems.forEach(e => {
                    const rect = e.getBoundingClientRect();
                    e.initialTop = scrollTop + rect.top;
                    e.initialBottom = scrollTop + rect.bottom;
                    e.initialHeight = rect.height;

                    if (windowWidth < 768) {
                        $(e).css({ position: "relative", height: `${e.initialHeight}` });
                    } else if (scrollTop >= e.initialTop && windowWidth >= 768) {
                        $(e).css({ position: "fixed", top: 0, width: "100%", height: `${e.initialHeight}px` });
                    } else if (scrollTop >= e.initialBottom || scrollTop < e.initialTop) {
                        $(e).css({ position: "relative", height: `${e.initialHeight}` });
                    }
                });

                $(window).off("resize", onWindowResize).on("resize", onWindowResize);

                if (windowWidth < 768) {
                    $(window).off("scroll", throttledFunction);
                } else {
                    $(window).off("scroll", throttledFunction).on("scroll", throttledFunction);
                }
            }
        }

    }

    /*
        private handleCarouselSwipe() {

            // Check if devide supports touch events
            if (('ontouchstart' in document.documentElement)) return;

            var $carousel = $('#homepage-slider'); // Replace with your Carousel ID or class
            var isDragging = false;
            var startingPos = 0;
            var endingPos = 0;

            $carousel.on('mousedown', function (e) {
                isDragging = true;
                startingPos = e.pageX;
            });

            $(document).on('mousemove', function (e) {
                if (isDragging) {
                    endingPos = e.pageX;
                }
            });

            $(document).on('mouseup', function (e) {
                if (isDragging) {
                    isDragging = false;
                    var distanceDragged = startingPos - endingPos;
                    if (distanceDragged > 50) { // Dragged to left
                        ($carousel as any).carousel('next');
                    } else if (distanceDragged < -50) { // Dragged to right
                        ($carousel as any).carousel('prev');
                    }
                }
                startingPos = 0;
                endingPos = 0;
            });
        }*/

    private handleFullHeightItems() {

        var that = this;

        function isMobileMenu() {
            let bkpoint = that.getActiveBreakpoint();
            return bkpoint == "xs" || bkpoint == "sm" || bkpoint == "md";
        }

        function setDivHeight() {
            let navbarHeight = $(".navbar").outerHeight();

            let wpadminbar = $("#wpadminbar");
            let adminBarOffset = wpadminbar.length > 0 ? wpadminbar.outerHeight() : 0;

            $('.fullheight').css('min-height', $(window).height() - (isMobileMenu() ? navbarHeight : 0) - adminBarOffset);

            $('.fullheight.first').css('min-height', $(window).height() - navbarHeight - adminBarOffset);
        }

        // Set the height initially
        setDivHeight();

        // Update the height on window resize
        $(window).on('resize', function () {
            setDivHeight();
        });
    }

    openPanel: HTMLElement = null;
    panelOpener: HTMLElement = null;

    private handleExpandPanels() {

        $('*[data-expand-panel]').on('click', item => {
            var panelOpener = item.currentTarget;
            var panelId = $(panelOpener).attr('data-expand-panel');

            var panel = $("#" + panelId);

            // VERY BAD FIX: If the click was inside the typeahead suggestion, do nothing
            if (item.target.className.indexOf('tt-suggestion') > -1) {
                return;
            }

            if (panel.length == 0) {
                this.log("Panel " + panelId + " not found");
                return;
            }

            // Check if we're opening the same panel that's already open (same ID) but with a different opener
            const isSamePanelDifferentOpener = this.openPanel != null &&
                this.openPanel.id === panelId &&
                this.panelOpener !== panelOpener;

            // If we're dealing with a nested trigger for the same panel, don't reposition the panel
            if (isSamePanelDifferentOpener) {
                // Just update the panel opener reference, but don't reposition or animate
                $(this.panelOpener).removeClass('active');
                this.panelOpener = panelOpener;
                $(this.panelOpener).addClass('active');
                return;
            }

            // Place the element .expand-panel-inner inside the panel element to the same horizontal position of the panel opener
            var panelInner = panel.find('.expand-panel-inner');

            var anchorId = $(panelOpener).attr('data-expand-panel-anchor');
            var stop = $(panelOpener).attr('data-expand-panel-stop');

            var panelOpenerRect: DOMRect;
            var forceWidth: number | null = null;

            if (anchorId) {
                let anchor = $("#" + anchorId);
                panelOpenerRect = anchor[0].getBoundingClientRect();

                var isForceWidth = $(panelOpener).attr('data-expand-panel-force-width') == '1' || $(panelOpener).attr('data-expand-panel-force-width') == 'true';

                if (isForceWidth) forceWidth = anchor.outerWidth();
            } else {
                panelOpenerRect = panelOpener.getBoundingClientRect();
            }

            var panelInnerRect = panelInner[0].getBoundingClientRect();

            if (this.openPanel != null) {
                if (this.panelOpener == panelOpener) {
                    // Chiude il pannello se si clicca sullo stesso opener
                    panel.fadeOut();
                    $(this.panelOpener).removeClass('active');
                    this.openPanel = null;
                    this.panelOpener = null;

                    this.log("Hiding panel " + panelId);
                } else {
                    // Cambia panel o opener: prima nasconde il pannello corrente
                    this.log("Hiding panel " + this.openPanel.id);

                    // Se stiamo aprendo un pannello diverso, usa fadeOut e poi fadeIn
                    if (this.openPanel.id !== panelId) {
                        $(this.openPanel).fadeOut(200, () => {
                            // Posiziona e mostra il nuovo pannello solo dopo che il vecchio è nascosto
                            panelInner.css('margin-left', panelOpenerRect.left - panelInnerRect.left + 'px');
                            if (forceWidth != null) panelInner.css('width', forceWidth + 'px');

                            this.log("Showing panel " + panelId);
                            panel.fadeIn(200);
                        });
                    } else {
                        // Stesso pannello, diverso opener - non serve rianimare
                        panelInner.css('margin-left', panelOpenerRect.left - panelInnerRect.left + 'px');
                        if (forceWidth != null) panelInner.css('width', forceWidth + 'px');
                    }

                    $(this.panelOpener).removeClass('active');

                    // Aggiorna i riferimenti
                    this.openPanel = panel[0];
                    this.panelOpener = panelOpener;
                    $(this.panelOpener).addClass('active');
                }
            } else {
                // Primo pannello da aprire
                this.openPanel = panel[0];
                this.panelOpener = panelOpener;

                panelInner.css('margin-left', panelOpenerRect.left - panelInnerRect.left + 'px');
                if (forceWidth != null) panelInner.css('width', forceWidth + 'px');
                $(this.panelOpener).addClass('active');

                this.log("Showing panel " + panelId);
                panel.fadeIn(200);
            }

        });

        let panels = $(".expand-panel");

        // Clicking anywhere in the document
        $(document).on("click", event => {

            // If the click was not inside the panel or the button that toggles the panel
            if (!$(event.target).closest(panels).length && !$(event.target).closest(this.panelOpener).length) {
                // Check if the panel is visible
                if (panels.is(":visible")) {
                    panels.fadeOut();
                    $(this.panelOpener).removeClass('active');
                    this.panelOpener = null;
                    this.openPanel = null;
                }
            }
        });

        // Add scroll event listener to close panels when scrolling
        let scrollTimeout: number;
        $(window).on('scroll', () => {
            if (scrollTimeout) {
                clearTimeout(scrollTimeout);
            }

            // Use a small timeout to throttle the scroll event
            scrollTimeout = setTimeout(() => {
                if (this.openPanel != null) {
                    this.hideExpandPanel();
                }
            }, 50) as unknown as number;
        });
    }

    public hideExpandPanel() {
        if (this.openPanel != null) {
            $(this.openPanel).fadeOut();
            $(this.panelOpener).removeClass('active');
            this.panelOpener = null;
            this.openPanel = null;
        }
    }

    /*
    private handleCollapseNavbar() {
        // When scrolling down, hide the navbar. When scrolling up, show the navbar
        var prevScrollpos = window.scrollY;

        const bkPoints = ["lg", "xl", "xxl"]

        var adminBar = $("#wpadminbar");
        var adminBarOffset = adminBar.length > 0 ? adminBar.height() : 0;

        window.onscroll = () => {
            let navbarHeight = $(".navbar").outerHeight();
            let currentScrollPos = window.scrollY;
            let bkpoint = this.getActiveBreakpoint();
            if (bkPoints.indexOf(bkpoint) > -1) {
                if (prevScrollpos > currentScrollPos || currentScrollPos < navbarHeight) {
                    $(".navbar").css('top', adminBarOffset);
                } else {
                    if (this.openPanel == null)
                        $(".navbar").css('top', -navbarHeight + adminBarOffset);
                }
            }
            prevScrollpos = currentScrollPos;
        };

    }
        */

    private handleOffcanvasNavbar() {

        $('#navbarSideCollapse').on('click', evt => {
            $('.offcanvas-collapse').toggleClass('open');
            $(evt.target).toggleClass('open');
            // Show or hide #backdrop
            $("#backdrop").fadeToggle();

            // Disable scrolling
            $('body').toggleClass('no-scroll');
        });

        // Find all data-bs-dismiss="custom-offcanvas"
        $('[data-bs-dismiss="custom-offcanvas"]').on('click', evt => {
            $('.offcanvas-collapse').removeClass('open');
            $('.navbar-toggler-icon').removeClass('open');
            $('#navbarSideCollapse').removeClass('open');
            $("#backdrop").fadeOut();

            $('body').toggleClass('no-scroll');
        });

        // Clicking anywhere in the document
        $(document).on("click", event => {

            let offcanvas = $('.offcanvas-collapse');

            if (offcanvas.hasClass('open')) {

                // If the click was not inside the panel or the button that toggles the panel
                if (!$(event.target).closest('.offcanvas-collapse').length && !$(event.target).closest('#navbarSideCollapse').length) {
                    offcanvas.removeClass('open');
                    $('.navbar-toggler-icon').removeClass('open');
                    $('#navbarSideCollapse').removeClass('open');
                    $("#backdrop").fadeOut();
                    $('body').toggleClass('no-scroll');
                }
            }

        });

    }

    public getActiveBreakpoint() {

        if ($("#show-xs").is(":visible")) return "xs";
        if ($("#show-sm").is(":visible")) return "sm";
        if ($("#show-md").is(":visible")) return "md";
        if ($("#show-lg").is(":visible")) return "lg";
        if ($("#show-xl").is(":visible")) return "xl";
        if ($("#show-xxl").is(":visible")) return "xxl";

        return "none";
    }

    private handleSmartSticky() {
        this.log("handleSmartSticky");

        var sticky = $(".smart-sticky");

        if (sticky.length == 0) return;

        for (var n = 0; n < sticky.length; n++) {

            var elem = sticky[n];

            // Get element top position
            var top = $(elem).offset().top;

            // Set element top position
            $(elem).css('top', top + 'px');
            $(elem).css('position', 'sticky');

        }

    }

    private initQuicklink() {
        this.log("initQuicklink")
        quicklink.listen({
            timeout: 1000,
            ignores: [
                /\/api\/?/,
                /\/wp-admin\//,
                /\/wp-json\//,
                /\/prenota\//,
                // Target url contains .php
                (uri, elem) => elem.hasAttribute('href') && elem.getAttribute('href').indexOf('.php') > -1,
                (uri, elem) => elem.hasAttribute('noprefetch'),
                // All pages that contain the word logout
                (uri, elem) => elem.hasAttribute('href') && elem.getAttribute('href').indexOf('logout') > -1,
                // All pages that contain the word checkout or my-account
                (uri, elem) => elem.hasAttribute('href') && (elem.getAttribute('href').indexOf('checkout') > -1 || elem.getAttribute('href').indexOf('my-account') > -1),
                // All pages that contain the word prenota
            ]
        });
    }

    /*private initWaypoints() {
        this.log("initWaypoints");
    }*/

    public log(...args: any[]) {
        if (this.info.debug) {
            if (typeof console !== "undefined" && typeof console.log !== "undefined")
                console.log(...args);
        }
    }

    public getParameters(rawNames: any) {

        if (typeof rawNames === 'undefined' || rawNames === null || rawNames === '')
            return null;

        let classNames: string;

        if (typeof rawNames === 'object') {
            if (rawNames.baseVal !== undefined)
                classNames = rawNames.baseVal;
            else if (rawNames.animVal !== undefined)
                classNames = rawNames.animVal;
            else
                return null;
        } else
            classNames = rawNames;

        var classes = classNames.split(/\s+/);

        for (var i = 0; i < classes.length; i++) {

            var cls = classes[i];

            if (startsWith(cls, 'animate-')) {

                var params = cls.split('-');

                if (params.length != 3) {
                    this.log('Found invalid class: ' + cls);
                    continue;
                }

                var offset = params[1] + '%';
                var animationClass = params[2];

                return { offset: offset, animationClass: animationClass, keepOnTrigger: false };
            }
        }

        return null;
    }

    // animate-{percent}-{animationClass}

    private handleWaypoint(ts, parameters) {

        return function () {
            this.log(ts);
            this.log('triggered');
            this.log(ts.element);

            $(ts.element).addClass(parameters.animationClass);

            if (!parameters.keepOnTrigger)
                ts.destroy();
        };
    }

    private setupScrollAnimations() {
        this.log("setupScrollAnimations");

        var elements = $("[class^='animate-'],[class*=' animate-']");

        for (var n = 0; n < elements.length; n++) {

            var elem = elements[n];

            var parameters = this.getParameters(elem.className);
            //log(parameters);

            if (parameters == null) continue;

            $(elem).css('opacity', '0').addClass('animated').waypoint({
                handler: (() => {

                    //var ts = this;
                    var parms = parameters;

                    return function () {
                        $(this.element).addClass(parms.animationClass);

                        if (!parms.keepOnTrigger)
                            this.destroy();
                    };
                })(),
                offset: parameters.offset
            });
        }
    }

    // Aggiunge la classe scrolled quando la pagina è scrollata e no-scroll quando non lo è
    private handleNoScroll() {

        function checkScroll() {
            var scroll = $(window).scrollTop();

            var body = $("body");
            // Siamo al top
            if (scroll == 0) {
                body.addClass('scroll-top');
                body.removeClass('scrolled');
            } else {
                body.removeClass('scroll-top');
                body.addClass('scrolled');
            }
        }

        checkScroll();

        $(window).on('scroll', function (event) {
            checkScroll();
        });
    }

    public getParameterByName(name: string, url: string): string {
        if (!url) url = window.location.href;
        name = name.replace(/[\[\]]/g, '\\$&');
        var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
            results = regex.exec(url);
        if (!results) return null;
        if (!results[2]) return '';
        return decodeURIComponent(results[2].replace(/\+/g, ' '));
    }

    public getScroll() {
        if (window.scrollY != undefined) {
            return { x: scrollX, y: scrollY };
        } else {
            var sx, sy, d = document,
                r = d.documentElement,
                b = d.body;
            sx = r.scrollLeft || b.scrollLeft || 0;
            sy = r.scrollTop || b.scrollTop || 0;
            return { x: sx, y: sy };
        }
    }

}

$(function () {
    const pageInfo: PageInfo = window["pageInfo"];

    // pageInfo.debug is set to true if the query url contains ?debug
    pageInfo.debug = pageInfo.debug || window.location.href.indexOf('?debug') > -1;

    let pageScript = new PageScript(pageInfo);
    pageScript.init();

    window["pageScript"] = pageScript;

    console.log("assigned pageScript to window");

});

function startsWith(str: string, prefix: string) {
    return str.indexOf(prefix) === 0;
}